import {
  Badge, Button, Card, ErrorState, Field, LARGEST_PAGE, LoadingState, Notice, PageHeader, Permissions, Row, Select,
  Stack, Textarea, complaintTone, isInternal, isMessage, severityTone, useApi, useI18n, usePermission,
  type ComplaintEventResponse, type ComplaintResponse, type ComplaintSeverity,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';

const SEVERITIES: ComplaintSeverity[] = ['Low', 'Normal', 'High'];

/**
 * Handling one complaint (§4.14).
 *
 * The screen's one real design problem is the difference between a note and a reply. An internal note
 * written into the reply box is a nursery's private assessment of a family, sent to that family; there is no
 * undo, and the API cannot tell the difference because both are just text. So the two are separated as far
 * as the layout allows — different cards, different headings, different button wording — and the reply box
 * says who will read it, every time, rather than once in a tooltip.
 *
 * The complaint the caller is the subject of never reaches here at all: the API answers 404 (FR-CMP-02), so
 * there is no state in this component for "you may not see this one".
 */
export function Complaint() {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  const { id: routeId } = useParams();
  const queryClient = useQueryClient();

  const id = Number(routeId);

  const canRespond = usePermission(Permissions.complaints.respond);
  const canAssign = usePermission(Permissions.complaints.assign);
  const canClose = usePermission(Permissions.complaints.close);

  const [note, setNote] = useState('');
  const [reply, setReply] = useState('');
  const [resolution, setResolution] = useState('');
  const [assignee, setAssignee] = useState('');

  const complaint = useQuery({
    queryKey: ['complaints', id],
    queryFn: () => api.complaints.get(id),
    enabled: Number.isFinite(id),
  });

  const timeline = useQuery({
    queryKey: ['complaints', id, 'timeline'],
    queryFn: () => api.complaints.timeline(id, { pageSize: LARGEST_PAGE }),
    enabled: Number.isFinite(id),
  });

  const assignees = useQuery({
    queryKey: ['complaints', id, 'assignees'],
    queryFn: () => api.complaints.assignees(id),
    enabled: Number.isFinite(id) && canAssign,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['complaints'] });
  };

  const acknowledge = useMutation({ mutationFn: () => api.complaints.acknowledge(id), onSuccess: refresh });
  const start = useMutation({
    mutationFn: () => api.complaints.update(id, { markInProgress: true }),
    onSuccess: refresh,
  });
  const setSeverity = useMutation({
    mutationFn: (severity: ComplaintSeverity) => api.complaints.update(id, { severity }),
    onSuccess: refresh,
  });
  const assign = useMutation({
    mutationFn: () => api.complaints.assign(id, Number(assignee)),
    onSuccess: async () => { setAssignee(''); await refresh(); },
  });
  const addNote = useMutation({
    mutationFn: () => api.complaints.addNote(id, note.trim()),
    onSuccess: async () => { setNote(''); await refresh(); },
  });
  const sendReply = useMutation({
    mutationFn: () => api.complaints.reply(id, reply.trim()),
    onSuccess: async () => { setReply(''); await refresh(); },
  });
  const resolve = useMutation({
    mutationFn: () => api.complaints.resolve(id, resolution.trim()),
    onSuccess: async () => { setResolution(''); await refresh(); },
  });
  const close = useMutation({ mutationFn: () => api.complaints.close(id), onSuccess: refresh });
  const reopen = useMutation({ mutationFn: () => api.complaints.reopen(id), onSuccess: refresh });

  if (complaint.isPending) return <LoadingState />;
  if (complaint.isError) {
    return <ErrorState error={complaint.error} onRetry={() => void complaint.refetch()} />;
  }

  const it: ComplaintResponse = complaint.data;
  const entries = timeline.data?.items ?? [];
  const open = it.status !== 'Closed' && it.status !== 'Withdrawn';

  return (
    <Stack>
      <PageHeader title={it.subject} lede={it.reference} />

      <Card>
        <Stack>
          <Row>
            <Badge tone={complaintTone(it.status)}>{t(`complaint.status.${it.status}`)}</Badge>
            <Badge tone={severityTone(it.severity)}>{t(`complaint.severity.${it.severity}`)}</Badge>
            <span className="dg-muted">{t(`complaint.category.${it.category}`)}</span>
            <span className="dg-muted">{formatDateTime(it.createdAt)}</span>
          </Row>

          <Row>
            <span className="dg-muted">{t('complaints.raisedBy')}</span>
            <strong>{it.submittedByName ?? '—'}</strong>
            {it.childName != null && <span className="dg-muted">· {it.childName}</span>}
          </Row>

          {/* The person the complaint names, stated plainly. Whoever is reading this is not that person —
              the API would not have served the record — so there is nothing to soften. */}
          {it.aboutName != null && (
            <Notice tone="warning">{t('complaints.about.is', { name: it.aboutName })}</Notice>
          )}

          <p>{it.body}</p>

          {it.resolutionSummary != null && (
            <Notice tone="success">
              <strong>{t('complaint.event.Resolved')}</strong>
              <br />
              {it.resolutionSummary}
            </Notice>
          )}
        </Stack>
      </Card>

      {canRespond && open && (
        <Card title={t('complaints.handling')}>
          <Stack>
            {[acknowledge, start, setSeverity, close, reopen].map((mutation, index) =>
              mutation.isError ? <ErrorState key={index} error={mutation.error} /> : null)}

            <Row>
              {it.acknowledgedAt == null && (
                <Button
                  type="button"
                  onClick={() => acknowledge.mutate()}
                  busy={acknowledge.isPending}
                  busyLabel={t('action.saving')}
                >
                  {t('complaints.acknowledge')}
                </Button>
              )}

              {(it.status === 'Assigned' || it.status === 'Acknowledged') && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => start.mutate()}
                  busy={start.isPending}
                  busyLabel={t('action.saving')}
                >
                  {t('complaints.start')}
                </Button>
              )}

              {canClose && it.status === 'Resolved' && (
                <>
                  <Button
                    type="button"
                    onClick={() => close.mutate()}
                    busy={close.isPending}
                    busyLabel={t('action.saving')}
                  >
                    {t('complaints.close')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => reopen.mutate()}
                    busy={reopen.isPending}
                    busyLabel={t('action.saving')}
                  >
                    {t('complaints.reopen')}
                  </Button>
                </>
              )}
            </Row>

            <Field label={t('complaint.severity')}>
              {(props) => (
                <Select
                  {...props}
                  value={it.severity}
                  onChange={(event) => setSeverity.mutate(event.target.value as ComplaintSeverity)}
                >
                  {SEVERITIES.map((option) => (
                    <option key={option} value={option}>{t(`complaint.severity.${option}`)}</option>
                  ))}
                </Select>
              )}
            </Field>
          </Stack>
        </Card>
      )}

      {canAssign && open && it.status !== 'Resolved' && (
        <Card title={t('complaints.assign')}>
          <Stack>
            {assign.isError && <ErrorState error={assign.error} />}

            {/* The list excludes the person the complaint is about — the API builds it, so the console
                cannot offer a name the assign call would then refuse (FR-CMP-07). */}
            <Field label={t('complaints.assignTo')} hint={t('complaints.assign.hint')}>
              {(props) => (
                <Select {...props} value={assignee} onChange={(event) => setAssignee(event.target.value)}>
                  <option value="">{t('complaints.assign.choose')}</option>
                  {(assignees.data ?? []).map((person) => (
                    <option key={person.membershipId} value={person.membershipId}>
                      {person.name ?? '—'} · {t('complaints.openCount', { count: person.openComplaints })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Row>
              <Button
                type="button"
                disabled={assignee === ''}
                onClick={() => assign.mutate()}
                busy={assign.isPending}
                busyLabel={t('action.saving')}
              >
                {it.assignedToMembershipId == null ? t('complaints.assign') : t('complaints.reassign')}
              </Button>
              {it.assignedToName != null && (
                <span className="dg-muted">
                  {t('complaints.currentlyWith', { name: it.assignedToName })}
                </span>
              )}
            </Row>
          </Stack>
        </Card>
      )}

      <Card title={t('complaints.timeline')}>
        {timeline.isPending && <LoadingState />}
        {timeline.isError && <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />}

        <Stack>
          {entries.map((entry) => (
            <TimelineEntry key={entry.id} entry={entry} formatDateTime={formatDateTime} t={t} />
          ))}
        </Stack>
      </Card>

      {canRespond && open && (
        <>
          {/* Two separate cards, deliberately. The one thing this screen must never do is let a private
              assessment of a family be typed into the box that sends it to them. */}
          <Card title={t('complaints.note')} hint={t('complaints.note.who')}>
            <NoteForm
              label={t('complaints.note')}
              hint={t('complaints.note.hint')}
              action={t('complaints.note.add')}
              value={note}
              onChange={setNote}
              onSubmit={() => addNote.mutate()}
              pending={addNote.isPending}
              error={addNote.error}
              savingLabel={t('action.saving')}
            />
          </Card>

          <Card title={t('complaints.reply')}>
            <Stack>
              <Notice tone="warning">{t('complaints.reply.warning')}</Notice>
              <NoteForm
                label={t('complaints.reply')}
                hint={t('complaints.reply.hint')}
                action={t('complaints.reply.send')}
                value={reply}
                onChange={setReply}
                onSubmit={() => sendReply.mutate()}
                pending={sendReply.isPending}
                error={sendReply.error}
                savingLabel={t('action.saving')}
              />
            </Stack>
          </Card>

          {it.status !== 'Resolved' && (
            <Card title={t('complaints.resolve')}>
              <Stack>
                <Notice tone="info">{t('complaints.resolve.hint')}</Notice>
                <NoteForm
                  label={t('complaints.resolve.summary')}
                  hint={t('complaints.resolve.summary.hint')}
                  action={t('complaints.resolve')}
                  value={resolution}
                  onChange={setResolution}
                  onSubmit={() => resolve.mutate()}
                  pending={resolve.isPending}
                  error={resolve.error}
                  savingLabel={t('action.saving')}
                />
              </Stack>
            </Card>
          )}
        </>
      )}
    </Stack>
  );
}

function TimelineEntry({
  entry, formatDateTime, t,
}: {
  entry: ComplaintEventResponse;
  formatDateTime: (value: string) => string;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const internal = isInternal(entry);

  return (
    <div className="dg-stack">
      <Row>
        <strong>{t(`complaint.event.${entry.eventType}`, { name: entry.subjectName ?? '' })}</strong>
        {/* Marked on every internal entry, not only on notes. Someone scanning the timeline before
            replying should never have to work out which lines the family can see. */}
        {internal && <Badge tone="neutral">{t('complaints.internal')}</Badge>}
        <span className="dg-muted">{entry.actorName ?? '—'}</span>
        <span className="dg-muted">{formatDateTime(entry.at)}</span>
      </Row>
      {isMessage(entry) && entry.body != null && <p>{entry.body}</p>}
    </div>
  );
}

function NoteForm({
  label, hint, action, value, onChange, onSubmit, pending, error, savingLabel,
}: {
  label: string;
  hint: string;
  action: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  pending: boolean;
  error: unknown;
  savingLabel: string;
}) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (value.trim()) onSubmit();
  };

  return (
    <form onSubmit={submit}>
      <Stack>
        {error !== null && error !== undefined && <ErrorState error={error} />}

        <Field label={label} hint={hint}>
          {(props) => (
            <Textarea {...props} rows={4} value={value} onChange={(event) => onChange(event.target.value)} />
          )}
        </Field>

        <Row>
          <Button type="submit" busy={pending} busyLabel={savingLabel}>{action}</Button>
        </Row>
      </Stack>
    </form>
  );
}
